import { useCallback, useMemo, useState } from "react";

import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ChangeApproverModal from "./ChangeApproverModal";
import { SampleDataOverlay } from "./DiscoveryHubSampleState";
import {
  leaderboardApproverOptionLabel,
  mapAdminUserToLeaderboardApprover,
} from "./leaderboardApproverUtils";
import { SAMPLE_LEADERBOARD_ROWS } from "./leaderboardSampleData";
import LeaderboardTable from "./LeaderboardTable";
import {
  sortLeaderboardRows,
  toggleLeaderboardSort,
  type LeaderboardRow,
  type LeaderboardSortState,
} from "./leaderboardTableUtils";
import LeaderboardTierKpiCards from "./LeaderboardTierKpiCards";
import Button from "../../Components/Button";
import { HubPageLoading } from "../../Components/EngagementEmptyPanel";
import Modal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchAdminUsers } from "../../Store/Reducers/MasterSlice";
import { submitApproval } from "../../Store/Reducers/SponsorEngagementSlice";
import {
  LB_ACTION_BAR_MSG,
  LB_EMPTY,
  LB_FILTER_LBL,
  LB_FILTERS,
  LB_PAGE_HEADER,
  LB_PAGE_SUB,
  LB_PAGE_TITLE,
  LB_PANEL,
  LB_QUARTER_DISPLAY,
  LB_QUARTER_VALUE,
  LB_STATUS_BANNER_BODY,
  LB_STATUS_BANNER_META,
  LB_STATUS_BANNER_TITLE,
  lbActionBarClass,
  lbStatusBannerClass,
} from "../../Utils/Helper/discoveryHubUi";
import {
  LEADERBOARD_TIER_ORDER,
  SPONSOR_ENGAGEMENT_THEATERS,
  formatQuarterPeriodLabel,
  rowMatchesTheaterFilter,
} from "../../Utils/Helper/sponsorEngagementUi";
import { toast } from "../../Utils/Helper/toast";

export type LeaderboardApprover = {
  user_id: string;
  full_name?: string;
  position?: string;
  email?: string;
  role_label?: string;
};

export type LeaderboardPayload = {
  period?: string;
  publication_state?: string | null;
  published_at?: string | null;
  submitted_at?: string | null;
  review_notes?: string | null;
  send_back_comments?: string | null;
  assigned_approver_id?: string | null;
  assigned_approver_name?: string | null;
  rows?: LeaderboardRow[];
};

type FilterState = {
  theater: string;
  region: string;
  tier: string;
  search: string;
};

type DraftLeaderboardPanelProps = {
  onSubmitted?: () => void | Promise<void>;
  onApproverChanged?: () => void | Promise<void>;
  currentQuarterPeriod?: string;
  leaderboard: LeaderboardPayload | null;
  onRefreshLeaderboard: () => Promise<void>;
  loading?: boolean;
  /** When true, pending submissions are reviewed in Approval Pending tab. */
  isAssignedApproverView?: boolean;
};

const DraftLeaderboardPanel = ({
  onSubmitted,
  onApproverChanged,
  currentQuarterPeriod,
  leaderboard,
  onRefreshLeaderboard,
  loading = false,
  isAssignedApproverView = false,
}: DraftLeaderboardPanelProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  const [approverModalMode, setApproverModalMode] = useState<"submit" | null>(
    null
  );
  const [changeApproverOpen, setChangeApproverOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [approverId, setApproverId] = useState("");
  const [approvers, setApprovers] = useState<LeaderboardApprover[]>([]);
  const [approversLoading, setApproversLoading] = useState(false);
  const [approverActionLoading, setApproverActionLoading] = useState(false);
  const [sort, setSort] = useState<LeaderboardSortState>({
    col: "rank",
    dir: "asc",
  });
  const [filters, setFilters] = useState<FilterState>({
    theater: "All",
    region: "All",
    tier: "All",
    search: "",
  });

  const rows = leaderboard?.rows ?? [];
  const pubState = leaderboard?.publication_state;
  const period = currentQuarterPeriod || leaderboard?.period || "";
  const isCurrentQuarter = Boolean(
    currentQuarterPeriod && period === currentQuarterPeriod
  );
  const isSample = rows.length === 0;
  const tableRows = isSample ? SAMPLE_LEADERBOARD_ROWS : rows;

  const regionOptions = useMemo(() => {
    const regions = new Set<string>();
    tableRows.forEach(r => {
      if (r.region) regions.add(r.region);
    });
    return ["All", ...Array.from(regions).sort()];
  }, [tableRows]);

  const filteredRows = useMemo(() => {
    const result = tableRows.filter(r => {
      if (!rowMatchesTheaterFilter(r.theater, filters.theater)) return false;
      if (filters.region !== "All" && r.region !== filters.region) return false;
      if (filters.tier !== "All" && r.tier !== filters.tier) return false;
      if (
        filters.search &&
        !(r.sponsor_name || "")
          .toLowerCase()
          .includes(filters.search.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
    return sortLeaderboardRows(result, sort);
  }, [tableRows, filters, sort]);

  const loadApprovers = async () => {
    setApproversLoading(true);
    const result = await dispatch(fetchAdminUsers());
    if (fetchAdminUsers.fulfilled.match(result)) {
      const users = Array.isArray(result.payload) ? result.payload : [];
      setApprovers(users.map(mapAdminUserToLeaderboardApprover));
    } else {
      toast.error((result.payload as string) || "Failed to load approvers");
      setApprovers([]);
    }
    setApproversLoading(false);
  };

  const closeApproverModal = () => {
    if (approverActionLoading) return;
    setApproverModalMode(null);
    setReviewNotes("");
    setApproverId("");
  };

  const openSubmitModal = async () => {
    setApproverId("");
    setReviewNotes("");
    setApproverModalMode("submit");
    await loadApprovers();
  };

  const handleSubmitApproval = async () => {
    if (!approverId) {
      toast.error("Select an approver");
      return;
    }
    setApproverActionLoading(true);
    try {
      const result = await dispatch(
        submitApproval({
          review_notes: reviewNotes,
          approver_id: approverId,
        })
      );
      if (submitApproval.rejected.match(result)) {
        toast.error((result.payload as string) || "Submit failed");
        return;
      }
      toast.success("Submitted for approval");
      closeApproverModal();
      await onRefreshLeaderboard();
      onSubmitted?.();
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Submit failed"
      );
    } finally {
      setApproverActionLoading(false);
    }
  };

  const goToDrilldown = (sponsorId: string) => {
    const returnTo = encodeURIComponent(
      `${location.pathname}${location.search}`
    );
    navigate(
      `${withOrg("/sponsor-drilldown")}?sponsorId=${sponsorId}&returnTo=${returnTo}`
    );
  };

  const goToPublishedLeaderboard = () => {
    // Capture the exact Discovery Hub view we're leaving so the breadcrumbs on
    // the Leaderboard page can navigate back and restore the Draft Leaderboard
    // tab state. location.pathname already includes the org prefix.
    const draftLeaderboardUrl = `${location.pathname}${location.search}`;
    const hubParams = new URLSearchParams(location.search);
    hubParams.set("hub", "ongoing");
    const discoveryHubUrl = `${location.pathname}?${hubParams.toString()}`;
    navigate(withOrg("/leaderboard"), {
      state: {
        fromDraftLeaderboard: true,
        breadcrumbDraftUrl: draftLeaderboardUrl,
        breadcrumbHubUrl: discoveryHubUrl,
      },
    });
  };

  const renderStatusBanner = () => {
    if (pubState === "published") {
      return (
        <div className={lbStatusBannerClass("published")}>
          <CheckCircle2 size={20} className="shrink-0" />
          <div className={LB_STATUS_BANNER_BODY}>
            <div className={LB_STATUS_BANNER_TITLE}>
              Leaderboard published
              {leaderboard?.published_at
                ? ` · ${new Date(leaderboard.published_at).toLocaleDateString()}`
                : ""}
            </div>
            <div className={LB_STATUS_BANNER_META}>
              Official rankings are on the Leaderboard tab next to Discovery
              Hub.
            </div>
          </div>
          <Button variant="primary" onClick={goToPublishedLeaderboard}>
            Open Leaderboard
            <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      );
    }

    if (pubState === "draft" && isCurrentQuarter) {
      return (
        <div className={lbActionBarClass()}>
          <p className={LB_ACTION_BAR_MSG}>
            <strong>Draft mode.</strong> Review the table, then submit for
            approval to make this snapshot official.
          </p>
          <Button variant="outline-primary" onClick={openSubmitModal}>
            Submit for Approval →
          </Button>
        </div>
      );
    }

    return null;
  };

  const renderBody = () => {
    if (loading) {
      return <HubPageLoading message="Loading leaderboard…" />;
    }

    if (isAssignedApproverView && pubState === "pending_approval") {
      return (
        <div className={LB_EMPTY}>
          <h3>Review in Approval Pending</h3>
          <p>
            This draft was submitted to you for approval. Open the Approval
            Pending tab to review rankings and publish or send back.
          </p>
        </div>
      );
    }

    if (pubState === "published" && isCurrentQuarter) {
      return null;
    }

    const displayRows = tableRows;

    return (
      <>
        {leaderboard?.send_back_comments ? (
          <div className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Approver send-back: {leaderboard.send_back_comments}
          </div>
        ) : null}

        <SampleDataOverlay
          active={isSample}
          message="Preview of the sponsor engagement leaderboard — real rankings appear once polls are completed and saved as a draft."
        >
          {isSample ? (
            <div className={lbActionBarClass()}>
              <p className={LB_ACTION_BAR_MSG}>
                <strong>Draft mode.</strong> Review the table, then submit for
                approval to make this snapshot official.
              </p>
              <Button variant="outline-primary" disabled>
                Submit for Approval →
              </Button>
            </div>
          ) : null}

          <LeaderboardTierKpiCards rows={displayRows} />

          <div className={LB_FILTERS}>
            <span className={LB_FILTER_LBL}>Filter</span>
            <select
              value={filters.theater}
              onChange={e =>
                setFilters(f => ({ ...f, theater: e.target.value }))
              }
            >
              <option value="All">All theaters</option>
              {SPONSOR_ENGAGEMENT_THEATERS.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={filters.region}
              onChange={e =>
                setFilters(f => ({ ...f, region: e.target.value }))
              }
            >
              {regionOptions.map(r => (
                <option key={r} value={r}>
                  {r === "All" ? "All regions" : r}
                </option>
              ))}
            </select>
            <select
              value={filters.tier}
              onChange={e => setFilters(f => ({ ...f, tier: e.target.value }))}
            >
              <option value="All">All tiers</option>
              {LEADERBOARD_TIER_ORDER.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              placeholder="Search sponsor…"
              value={filters.search}
              onChange={e =>
                setFilters(f => ({ ...f, search: e.target.value }))
              }
            />
            {period ? (
              <div className={LB_QUARTER_DISPLAY}>
                <span className={LB_FILTER_LBL}>Quarter</span>
                <span className={LB_QUARTER_VALUE}>
                  {formatQuarterPeriodLabel(period)}
                </span>
              </div>
            ) : null}
          </div>

          <LeaderboardTable
            rows={filteredRows}
            sortable
            sort={sort}
            onSortToggle={col =>
              setSort(prev => toggleLeaderboardSort(prev, col))
            }
            onRowClick={
              isSample
                ? undefined
                : row => row.sponsor_id && goToDrilldown(row.sponsor_id)
            }
          />
        </SampleDataOverlay>
      </>
    );
  };

  return (
    <div className={LB_PANEL}>
      <div className={LB_PAGE_HEADER}>
        <h2 className={LB_PAGE_TITLE}>Sponsor Engagement Leaderboard</h2>
        <p className={LB_PAGE_SUB}>
          {isSample ? SAMPLE_LEADERBOARD_ROWS.length : rows.length} sponsors ·
          2-factor composite (Team Feedback, Engagement Activity)
        </p>
      </div>

      {renderStatusBanner()}
      {renderBody()}

      <ChangeApproverModal
        isOpen={changeApproverOpen}
        onClose={() => setChangeApproverOpen(false)}
        onSuccess={async () => {
          if (onApproverChanged) {
            await onApproverChanged();
          } else {
            await onRefreshLeaderboard();
            await onSubmitted?.();
          }
        }}
        initialApproverId={leaderboard?.assigned_approver_id}
      />

      <Modal
        isOpen={approverModalMode === "submit"}
        toggle={closeApproverModal}
        title="Submit Draft Leaderboard for Approval"
        subtitle="Choose an approver and add optional review notes."
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeApproverModal}
              disabled={approverActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitApproval}
              loading={approverActionLoading}
              disabled={!approverId || approversLoading}
            >
              Submit for Approval
            </Button>
          </>
        }
      >
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Approver <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm text-gray-900 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
          value={approverId}
          onChange={e => setApproverId(e.target.value)}
          disabled={approversLoading || approverActionLoading}
        >
          <option value="">
            {approversLoading ? "Loading approvers…" : "Select approver"}
          </option>
          {approvers.map(a => (
            <option key={a.user_id} value={a.user_id}>
              {leaderboardApproverOptionLabel(a)}
            </option>
          ))}
        </select>
        {!approversLoading && approvers.length === 0 ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            No approvers available. Choose an Admin, C-Level, or Executive
            Leader user with approval access.
          </p>
        ) : null}
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Review notes{" "}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          className="w-full min-h-[120px] resize-y border border-gray-300 rounded-lg p-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
          placeholder="Highlights, anomalies, or context the approver should know before approving."
          value={reviewNotes}
          onChange={e => setReviewNotes(e.target.value)}
          disabled={approverActionLoading}
        />
      </Modal>
    </div>
  );
};

export default DraftLeaderboardPanel;
